import React, { useEffect, useState } from 'react';

import { ArrowLeft, Edit, Save, Trash2 } from 'lucide-react';

import {
	type CustomRates,
	type CustomRatesConfig,
	useCreateCustomRatesMutation,
	useGetCustomRatesQuery,
	useGetDefaultRatesQuery,
	useUpdateCustomRatesMutation,
} from '../api';
import { useToast } from '../context/ToastContext';
import { useConfigs } from '../hooks/useConfigs';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../providers/AppAuthProvider';
import { useTypedSelector } from '../store';

import Loader from './Loader';

interface MerchantConfigProps {
	onBack: () => void;
	configId?: string;
	onboardingMode?: boolean;
	onNavigateToOnboarding?: () => void;
	onOnboardingComplete?: () => void;
}

interface CardTypeRate {
	id: string; // Add unique ID for each card type
	name: string; // Card type name
	discount_rate: number; // Make required with default 0
	authorization: number; // Make required with default 0
	tiered_interchange: number; // Make required with default 0
	transaction_fee?: number; // Keep optional
	dues_and_assessments: number; // Make required with default 0
}

interface ApiRateData {
	discount_rate: number;
	authorization_fee: number;
	tiered_interchange: number;
	dues_and_assessments: number;
}

interface RateInputProps {
	cardId: string;
	field: keyof Omit<CardTypeRate, 'id' | 'name'>;
	initialValue?: string | number;
	isLoading?: boolean;
	onUpdate: (
		cardId: string,
		field: keyof Omit<CardTypeRate, 'id' | 'name'>,
		value: number | null,
	) => void;
	readOnly?: boolean;
}

export const RateInput: React.FC<RateInputProps> = ({
	cardId,
	field,
	initialValue = '',
	isLoading = false,
	onUpdate,
	readOnly = false,
}) => {
	const [inputValue, setInputValue] = useState(String(initialValue));

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;
		// Allow empty string
		if (raw === '') {
			setInputValue(raw);
			return;
		}

		// Only allow numbers, decimal point, and backspace
		const decimalRegex = /^\d*\.?\d*$/;
		if (decimalRegex.test(raw)) {
			// Check if the decimal part has more than 4 digits (allow at least 4 decimal places for card brand fees like APF = 0.0195)
			const parts = raw.split('.');
			if (parts.length === 1 || parts[1].length <= 4) {
				setInputValue(raw);
			}
		}
	};

	const handleBlur = () => {
		// Store the exact value entered, preserving decimal places (up to 4 for card brand fees like APF = 0.0195)
		const parsed = parseFloat(inputValue);
		if (!isNaN(parsed)) {
			// Preserve up to 4 decimal places, keep trailing zeros for display consistency
			const fixedValueString = parsed.toFixed(4);
			setInputValue(fixedValueString);
			onUpdate(cardId, field, parseFloat(fixedValueString));
		} else {
			setInputValue('');
			onUpdate(cardId, field, null);
		}
	};

	const getPlaceholder = () => '0.0000';

	const getAriaLabel = () => {
		switch (field) {
			case 'discount_rate':
				return 'Discount Rate';
			case 'authorization':
				return 'Authorization Fee';
			case 'tiered_interchange':
				return 'Tiered Interchange Rate';
			case 'dues_and_assessments':
				return 'Dues and Assessments Rate';
			default:
				return 'Rate';
		}
	};

	return (
		<input
			type="text"
			inputMode="decimal"
			value={inputValue}
			onChange={handleChange}
			onBlur={handleBlur}
			disabled={isLoading}
			aria-label={getAriaLabel()}
			readOnly={readOnly}
			placeholder={getPlaceholder()}
			className={`w-24 px-3 py-2 border border-gray-300 rounded-md ${!readOnly ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : ''} text-sm disabled:opacity-50 disabled:cursor-not-allowed read-only:cursor-not-allowed appearance-none`}
		/>
	);
};

const MerchantConfig: React.FC<MerchantConfigProps> = ({
	onBack,
	configId,
	onboardingMode = false,
	onNavigateToOnboarding,
	onOnboardingComplete,
}) => {
	const authState = useTypedSelector((state) => state.mfeAuth);
	const { refreshConfigs } = useConfigs();
	const {
		completeCurrentStep,
		completeStepById,
		isStepCompleted,
		startedAt,
		onboardingState,
	} = useOnboarding();
	const { showToast } = useToast();
	const { isAdmin } = useAuth();
	const [isEditing, setIsEditing] = useState(!configId && isAdmin); // Start in edit mode for new configs only if admin
	const [merchantName, setMerchantName] = useState('');
	const [description, setDescription] = useState('');
	const [isDefault, setIsDefault] = useState(false);
	const [nameError, setNameError] = useState('');
	const [customRates, setCustomRates] = useState<CardTypeRate[]>([]);

	const [createCustomRates, { isLoading: isCreating }] =
		useCreateCustomRatesMutation();
	const [updateCustomRates, { isLoading: isUpdating }] =
		useUpdateCustomRatesMutation();
	const {
		data: existingConfig,
		isLoading: isLoadingConfig,
		error: configError,
	} = useGetCustomRatesQuery(configId || '', {
		skip: !configId,
	});
	const {
		data: defaultRates,
		isLoading: isLoadingDefaultRatesFromAPI,
		refetch: refetchDefaultRates,
	} = useGetDefaultRatesQuery();

	// Load existing config if editing
	useEffect(() => {
		if (existingConfig && configId) {
			if (!existingConfig.name && !existingConfig.description) {
				return;
			}

			setMerchantName(existingConfig.name);
			setDescription(existingConfig.description);
			setIsDefault(existingConfig.is_default);
			const ratesData =
				existingConfig.preference_data || existingConfig.custom_rates;

			// Convert object format to array format
			if (ratesData && typeof ratesData === 'object') {
				const ratesArray: CardTypeRate[] = Object.entries(ratesData).map(
					([name, rates], index) => {
						// Convert decimal percentages to percentages for display and map fields correctly
						const displayRates = {
							discount_rate: rates.discount_rate
								? rates.discount_rate * 100
								: 0,
							authorization: rates.authorization_fee || 0, // Map authorization_fee to authorization
							tiered_interchange: rates.tiered_interchange
								? rates.tiered_interchange * 100
								: 0,
							dues_and_assessments: rates.dues_and_assessments
								? rates.dues_and_assessments * 100
								: 0,
						};

						return {
							id: `existing-${index}`,
							name,
							...displayRates,
						};
					},
				);
				setCustomRates(ratesArray);
			}
		} else if (!configId && defaultRates) {
			// For new configs, use default rates from RTK Query

			const ratesArray: CardTypeRate[] = Object.entries(
				defaultRates.custom_rates,
			).map(([name, rates], index) => {
				// Convert decimal percentages to percentages for display (multiply by 100) and ensure proper field mapping
				const displayRates = {
					discount_rate: rates.discount_rate ? rates.discount_rate * 100 : 0,
					authorization: rates.authorization_fee || 0, // Map authorization_fee to authorization
					tiered_interchange: rates.tiered_interchange
						? rates.tiered_interchange * 100
						: 0,
					dues_and_assessments: rates.dues_and_assessments
						? rates.dues_and_assessments * 100
						: 0,
				};

				return {
					id: `default-${index}`,
					name: name.toLowerCase().replace(/_/g, ' '), // Convert VISA_DEBIT to "visa debit"
					...displayRates,
				};
			});

			setCustomRates(ratesArray);
		}
	}, [existingConfig, configId, defaultRates]);

	// Refresh configs in store when component mounts
	useEffect(() => {
		refreshConfigs();
	}, []);

	const isLoading =
		isCreating || isUpdating || isLoadingConfig || isLoadingDefaultRatesFromAPI;

	// Check if non-admin user is trying to create a new config
	if (!configId && !isAdmin) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-8 text-center">
					<div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
						<Edit className="h-8 w-8 text-red-600" />
					</div>
					<h3 className="text-xl font-bold text-gray-900 mb-4">
						Access Denied
					</h3>
					<p className="text-gray-600 mb-6">
						You need admin or organization admin privileges to create new
						configurations.
					</p>
					<button
						onClick={onBack}
						className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
					>
						Back to Configurations
					</button>
				</div>
			</div>
		);
	}

	if (isLoadingConfig && configId) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<Loader size="lg" text="Loading configuration..." />
			</div>
		);
	}

	if (isLoadingDefaultRatesFromAPI && !configId) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<Loader size="lg" text="Loading default rates..." />
			</div>
		);
	}

	// Show loading if we don't have default rates for new configs
	if (!configId && !defaultRates && !isLoadingDefaultRatesFromAPI) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<Loader size="lg" text="Loading default rates..." />
			</div>
		);
	}

	const handleSave = async () => {
		// Check admin permissions
		if (!isAdmin) {
			showToast(
				'Access Denied',
				'Admin access required to save configurations',
				'error',
			);
			return;
		}

		// Validate required fields
		if (!merchantName.trim()) {
			setNameError('Configuration name is required');
			return;
		}
		setNameError('');

		try {
			// Convert array to object format for API
			const customRatesObject: Record<string, ApiRateData> = {};
			customRates.forEach((card) => {
				const { id, name, ...rates } = card;

				// Convert percentage fields to decimals for API and ensure proper field mapping
				const apiRates = {
					discount_rate: rates.discount_rate
						? parseFloat((rates.discount_rate / 100).toFixed(6))
						: 0,
					authorization_fee: rates.authorization || 0, // Map authorization to authorization_fee for API
					tiered_interchange: rates.tiered_interchange
						? parseFloat((rates.tiered_interchange / 100).toFixed(6))
						: 0,
					dues_and_assessments: rates.dues_and_assessments
						? parseFloat((rates.dues_and_assessments / 100).toFixed(6))
						: 0,
				};

				customRatesObject[name] = apiRates;
			});

			const configToSave: CustomRatesConfig = {
				name: merchantName,
				description: description,
				is_default: isDefault,
				custom_rates: customRatesObject as CustomRates,
			};

			if (configId) {
				await updateCustomRates({
					id: configId,
					config: configToSave,
				}).unwrap();

				showToast('Success', 'Configuration updated successfully!', 'success');
				setIsEditing(false);

				refreshConfigs();

				// Auto-complete the config step when configuration is saved
				if (startedAt && !isStepCompleted(3)) {
					completeStepById(3);
				}

				if (onboardingMode && onOnboardingComplete) {
					setTimeout(() => {
						onOnboardingComplete();
					}, 1500);
				}
			} else {
				const createdConfig = await createCustomRates(configToSave).unwrap();

				showToast('Success', 'Configuration created successfully!', 'success');
				setIsEditing(false);

				refreshConfigs();

				// Auto-complete the config step when configuration is created
				if (startedAt && !isStepCompleted(3)) {
					completeStepById(3);
				}

				setTimeout(() => {
					onBack();
				}, 1500);
			}
		} catch (error) {
			console.error('Failed to save config:', error);
		}
	};

	const handleEdit = () => {
		if (!isAdmin) {
			showToast(
				'Access Denied',
				'Admin access required to edit configurations',
				'error',
			);
			return;
		}
		setIsEditing(true);
	};

	const handleCancel = () => {
		if (configId) {
			// Reset to original values for existing configs
			if (existingConfig) {
				setMerchantName(existingConfig.name);
				setDescription(existingConfig.description);
				setIsDefault(existingConfig.is_default);
				const ratesData =
					existingConfig.preference_data || existingConfig.custom_rates;

				// Convert object format to array format
				if (ratesData && typeof ratesData === 'object') {
					const ratesArray: CardTypeRate[] = Object.entries(ratesData).map(
						([name, rates], index) => {
							// Convert decimal percentages to percentages for display and map fields correctly
							const displayRates = {
								discount_rate: rates.discount_rate
									? rates.discount_rate * 100
									: 0,
								authorization: rates.authorization_fee || 0, // Map authorization_fee to authorization
								tiered_interchange: rates.tiered_interchange
									? rates.tiered_interchange * 100
									: 0,
								dues_and_assessments: rates.dues_and_assessments
									? rates.dues_and_assessments * 100
									: 0,
							};

							return {
								id: `existing-${index}`,
								name,
								...displayRates,
							};
						},
					);
					setCustomRates(ratesArray);
				}
			}
			setIsEditing(false);
		} else {
			// For new configs, go back to the list since there's nothing to save
			onBack();
		}
	};

	const addNewCardType = () => {
		const newCardType = `New Card Type ${customRates.length + 1}`;
		setCustomRates((prev) => [
			...prev,
			{
				id: `new-${Date.now()}`, // Generate a unique ID for new items
				name: newCardType,
				discount_rate: 0,
				authorization: 0,
				tiered_interchange: 0,
				dues_and_assessments: 0,
			},
		]);
	};

	const removeCardType = (id: string) => {
		setCustomRates((prev) => prev.filter((card) => card.id !== id));
	};

	const updateCardTypeName = (id: string, newName: string) => {
		if (customRates.find((card) => card.id === id)?.name === newName) return;

		setCustomRates((prev) =>
			prev.map((card) => (card.id === id ? { ...card, name: newName } : card)),
		);
	};

	const updateRate = (
		id: string,
		field: keyof Omit<CardTypeRate, 'id' | 'name'>,
		value: string,
	) => {
		if (!value || value === '') {
			setCustomRates((prev) =>
				prev.map((card) =>
					card.id === id
						? {
								...card,
								[field]: 0,
							}
						: card,
				),
			);
			return;
		}

		// Parse the number value and fix to 4 decimal places (needed for card brand fees like APF = 0.0195)
		const numValue = parseFloat(value);
		if (isNaN(numValue)) {
			return; // Don't update if invalid number
		}

		const fixedValue = parseFloat(numValue.toFixed(4));

		// For percentage fields, store as percentage (not decimal)
		if (
			field === 'discount_rate' ||
			field === 'tiered_interchange' ||
			field === 'dues_and_assessments'
		) {
			setCustomRates((prev) =>
				prev.map((card) =>
					card.id === id
						? {
								...card,
								[field]: fixedValue, // Store as percentage (e.g., 0.70 for 0.70%)
							}
						: card,
				),
			);
		} else {
			// For non-percentage fields, store as is
			setCustomRates((prev) =>
				prev.map((card) =>
					card.id === id
						? {
								...card,
								[field]: fixedValue,
							}
						: card,
				),
			);
		}
	};

	const getRateValue = (
		id: string,
		field: keyof Omit<CardTypeRate, 'id' | 'name'>,
	) => {
		const card = customRates.find((card) => card.id === id);
		const value = card?.[field];

		// Handle undefined, null, or NaN values
		if (value === undefined || value === null || isNaN(value as number)) {
			return '0.0000';
		}
		// Always display with 4 decimal places for consistency (needed for card brand fees like APF = 0.0195)
		const formattedValue = (value as number).toFixed(4);
		return formattedValue;
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
			{/* Loading Overlay */}
			{isLoading && (
				<div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 flex items-center space-x-3">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
						<span className="text-gray-700">
							{isCreating
								? 'Creating configuration...'
								: isUpdating
									? 'Updating configuration...'
									: isLoadingDefaultRatesFromAPI
										? 'Loading default rates...'
										: 'Loading...'}
						</span>
					</div>
				</div>
			)}

			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<button
							onClick={() => {
								if (onboardingMode) {
									if (onNavigateToOnboarding) {
										onNavigateToOnboarding();
									} else {
										window.location.href = '/onboarding';
									}
								} else {
									onBack();
								}
							}}
							className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
							title={onboardingMode ? 'Back to Onboarding' : 'Back'}
						>
							<ArrowLeft className="w-5 h-5" />
						</button>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">
								{configId ? 'Edit Configuration' : 'New Configuration'}
							</h1>
							<p className="text-gray-600 mt-1">
								{configId
									? 'Update your merchant configuration settings'
									: onboardingMode
										? 'Create a new merchant configuration for onboarding'
										: 'Create a new merchant configuration'}
							</p>
						</div>
					</div>
					{
						<div className="flex items-center space-x-3">
							{isEditing ? (
								<>
									<button
										onClick={handleSave}
										disabled={isLoading}
										className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium rounded-lg transition-colors"
									>
										{isLoading ? (
											<>
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
												Updating...
											</>
										) : (
											<>
												<Save className="w-4 h-4 mr-2" />
												Save Changes
											</>
										)}
									</button>
									<button
										onClick={handleCancel}
										disabled={isLoading}
										className="flex items-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
									>
										Cancel
									</button>
								</>
							) : (
								<div className="flex items-center space-x-3">
									{configId && (
										<button
											onClick={handleEdit}
											disabled={!isAdmin}
											className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
												isAdmin
													? 'bg-blue-600 hover:bg-blue-700 text-white'
													: 'bg-gray-300 text-gray-500 cursor-not-allowed'
											}`}
											title={
												isAdmin ? 'Edit configuration' : 'Admin access required'
											}
										>
											<Edit className="w-4 h-4 mr-2" />
											Edit Configuration
										</button>
									)}
								</div>
							)}
						</div>
					}
				</div>
			</div>

			{/* Main Content */}
			<div className="space-y-8">
				{/* Basic Info Section */}
				<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
					<div className="space-y-6">
						{/* Merchant Name */}
						<div className="flex items-center space-x-4">
							<div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
								<span className="text-white font-bold text-lg">
									{merchantName ? merchantName.charAt(0).toUpperCase() : 'C'}
								</span>
							</div>
							<div className="flex-1">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Configuration Name <span className="text-red-500">*</span>
								</label>
								{isEditing ? (
									<div>
										<input
											type="text"
											value={merchantName}
											onChange={(e) => {
												setMerchantName(e.target.value);
												if (nameError && e.target.value.trim()) {
													setNameError('');
												}
											}}
											placeholder="Enter configuration name"
											disabled={isLoading}
											className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
												nameError
													? 'border-red-300 bg-red-50'
													: 'border-gray-300'
											}`}
										/>
										{nameError && (
											<p className="mt-1 text-sm text-red-600">{nameError}</p>
										)}
									</div>
								) : (
									<div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
										{merchantName || 'N/A'}
									</div>
								)}
							</div>
						</div>

						{/* Description */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Description
							</label>
							{isEditing ? (
								<textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Enter description for this configuration"
									rows={2}
									disabled={isLoading}
									className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 resize-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
								/>
							) : (
								<div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 min-h-[70px]">
									{description || 'N/A'}
								</div>
							)}
						</div>

						{/* Default Toggle */}
						<div className="flex items-center">
							<input
								type="checkbox"
								id="isDefault"
								checked={isDefault}
								onChange={(e) => setIsDefault(e.target.checked)}
								disabled={!isEditing || isLoading}
								className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 appearance-none"
							/>
							<label
								htmlFor="isDefault"
								className="ml-2 block text-sm text-gray-900"
							>
								Set as default configuration
							</label>
						</div>
					</div>
				</div>

				{/* Card Types Section */}
				<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
					<div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
						<h2 className="text-lg font-semibold text-gray-900">
							Card Type Rates
						</h2>
					</div>
					{!configId && (
						<div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
							<p className="text-sm text-blue-700">
								💡 The rates are default rates. You can edit them to your needs.
							</p>
						</div>
					)}

					<div className="overflow-x-auto">
						<table className="min-w-full table-fixed">
							<thead className="bg-gray-50">
								<tr>
									<th className="w-40 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Card Type
									</th>
									<th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Discount Rate (%)
									</th>
									<th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Authorization Fee ($)
									</th>
									<th className="w-36 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Tiered Interchange Rate (%)
									</th>
									<th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Assessments Rate (%)
									</th>
									{isEditing && (
										<th className="w-20 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									)}
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{customRates.map((card, index) => (
									<tr key={card.id} className="hover:bg-gray-50">
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="w-full">
												<input
													type="text"
													value={card.name}
													onChange={(e) =>
														updateCardTypeName(card.id, e.target.value)
													}
													disabled={true}
													className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium disabled:cursor-not-allowed appearance-none bg-gray-50"
												/>
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="flex justify-center">
												<RateInput
													cardId={card.id}
													field="discount_rate"
													initialValue={getRateValue(card.id, 'discount_rate')}
													isLoading={isLoading}
													onUpdate={(id, field, value) => {
														updateRate(id, field, value?.toString() || '');
													}}
													readOnly={!isEditing}
												/>
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="flex justify-center">
												<RateInput
													cardId={card.id}
													field="authorization"
													initialValue={getRateValue(card.id, 'authorization')}
													isLoading={isLoading}
													onUpdate={(id, field, value) => {
														updateRate(id, field, value?.toString() || '');
													}}
													readOnly={!isEditing}
												/>
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="flex justify-center">
												<RateInput
													cardId={card.id}
													field="tiered_interchange"
													initialValue={getRateValue(
														card.id,
														'tiered_interchange',
													)}
													isLoading={isLoading}
													onUpdate={(id, field, value) => {
														updateRate(id, field, value?.toString() || '');
													}}
													readOnly={!isEditing}
												/>
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="flex justify-center">
												<RateInput
													cardId={card.id}
													field="dues_and_assessments"
													initialValue={getRateValue(
														card.id,
														'dues_and_assessments',
													)}
													isLoading={isLoading}
													onUpdate={(id, field, value) => {
														updateRate(id, field, value?.toString() || '');
													}}
													readOnly={!isEditing}
												/>
											</div>
										</td>
										{isEditing && (
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex justify-center">
													<button
														onClick={() => removeCardType(card.id)}
														className="text-red-600 hover:text-red-900 p-1"
														title="Remove card type"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MerchantConfig;
